import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useBrand } from '../../contexts/BrandContext.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { api } from '../../services/api/client.js';
import { CONNECTABLE_PLATFORMS, platformDisplayName } from '../../lib/platforms.js';
import { canEditManualDraft, manualDraftForm, manualDraftPayload, manualSchedulePayload, manualAccountReady } from '../../lib/manualDraft.js';
import { freePublishingState } from '../../lib/freePublishing.js';
import { MAX_VIDEO_BYTES, MAX_VIDEO_SECONDS, VIDEO_ACCEPT_SET, readVideoMetaAndPoster } from '../../lib/video-upload.js';
const unwrap = value => value?.data || value;
const imageTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
const fileData = file => new Promise((resolve, reject) => {
  const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(file);
});
const emptyForm = { brandProfileId: '', caption: '', platform: 'instagram', media: [] };
const validCreateKey = /^[A-Za-z0-9_-]{16,100}$/;
const pendingCreateStorageKey = (user, brandId) => `dathent.manualDraft.pending.${JSON.stringify([user.id, user.organizationId ?? null, brandId])}`;
function readPendingCreate(user, brandId) {
  const storage = window.sessionStorage;
  const storageKey = pendingCreateStorageKey(user, brandId);
  const serialized = storage.getItem(storageKey);
  if (serialized === null) return null;
  const record = JSON.parse(serialized);
  const payload = record?.payload;
  if (!validCreateKey.test(record?.key || '') || !payload || payload.brandProfileId !== brandId
    || typeof payload.caption !== 'string' || !payload.caption.trim() || payload.caption.length > 10000
    || !CONNECTABLE_PLATFORMS.some(platform => platform.id === payload.platform)
    || !Array.isArray(payload.mediaAssetIds) || payload.mediaAssetIds.length > 10
    || payload.mediaAssetIds.some(id => typeof id !== 'string' || !id.trim())
    || new Set(payload.mediaAssetIds).size !== payload.mediaAssetIds.length
    || payload.allowTextOnly !== (payload.mediaAssetIds.length === 0)
    || !Array.isArray(payload.hashtags) || payload.hashtags.length
    || Object.keys(payload).some(key => !['brandProfileId', 'caption', 'platform', 'mediaAssetIds', 'allowTextOnly', 'hashtags'].includes(key))) {
    throw new Error('Invalid pending draft');
  }
  return { key: record.key, payload, serialized, storageKey, storage };
}
// TikTok publishing requires its separate creator/privacy/consent flow. The
// manual scheduler contract does not carry those options; keep that path off.
const canPublishAccount = (account, registry) => account?.platform !== 'tiktok' && manualAccountReady(account, registry);

export default function useOwnDraftController({ id, capability, enabled = true, accessAvailable = true }) {
  const { user } = useAuth();
  const brand = useBrand();
  const dash = useDashboardData();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [raw, setRaw] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const formRef = useRef(form); formRef.current = form;
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [saved, setSaved] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [saveBlocked, setSaveBlocked] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [allowance, setAllowance] = useState(capability);
  const platformChosen = useRef(false);
  const running = useRef(false);
  // A create may have committed even when its response was lost. Keep its
  // identity and original input across explicit retries. Edits made while its
  // result is unknown are applied only after the saved receipt is recovered.
  const createKey = useRef(null);
  const localEdits = useRef(false);
  const identity = JSON.stringify([user?.id, user?.organizationId]);
  const owner = useRef(identity); owner.current = identity;
  const mounted = useRef(false);
  const current = () => mounted.current && owner.current === identity;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { createKey.current = null; localEdits.current = false; setRecovering(false); setNotice(null); }, [identity]);
  useEffect(() => { setAllowance(capability); }, [capability]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        if (id) {
          const payload = unwrap(await api.content.get(id, { fresh: true }));
          if (cancelled || !current()) return;
          if (!canEditManualDraft(payload)) { setBlocked(true); return; }
          const next = manualDraftForm(payload);
          setRaw(payload); setForm(next); setSaved(true);
          if (next.hasUnresolvedMedia) { setBlocked(true); setError('mediaUnavailable'); }
        } else {
          const data = unwrap(await api.brands.list());
          if (cancelled || !current()) return;
          const brands = data?.brands || [];
          const requested = params.get('brand');
          const brand = requested ? brands.find(b => b.id === requested)
            : brands.find(b => b.id === data.activeBrandId) || brands[0];
          if (requested && !brand) { setBlocked(true); setError('loadError'); return; }
          setForm({ ...emptyForm, brandProfileId: brand?.id || '' });
          if (brand) {
            createKey.current = readPendingCreate(user, brand.id);
            if (createKey.current) { setRecovering(true); setNotice('saveUnconfirmed'); }
          }
        }
      } catch { if (!cancelled && current()) { setError('loadError'); setBlocked(true); } }
      finally { if (!cancelled && current()) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [id, identity, enabled]);
  useEffect(() => {
    if (!enabled || !form.brandProfileId) return;
    let cancelled = false;
    Promise.all([api.socialAccounts.list({ brandProfileId: form.brandProfileId }), api.platforms.registry()]).then(([result, platforms]) => {
      if (cancelled || !current()) return;
      const available = (unwrap(result)?.accounts || []).filter(account => account.status === 'connected'
        && CONNECTABLE_PLATFORMS.some(p => p.id === account.platform));
      setAccounts(available);
      const entries = unwrap(platforms)?.platforms || [];
      setRegistry(entries);
      const ready = available.filter(account => canPublishAccount(account, entries));
      const soleAccount = ready.length === 1 ? ready[0] : null;
      if (!id && !formRef.current.expectedVersionId && !platformChosen.current && soleAccount) {
        // Functional update preserves caption/media typed while accounts load.
        setForm(value => ({ ...value, platform: soleAccount.platform }));
        setAccountId(soleAccount.id);
        setConfirmation(null);
      } else {
        setAccountId(!platformChosen.current && soleAccount?.platform === formRef.current.platform ? soleAccount.id : '');
      }
    }).catch(() => { if (!cancelled && current()) { setAccounts([]); setRegistry([]); setError('loadError'); } });
    return () => { cancelled = true; };
  }, [form.brandProfileId, identity, enabled]);

  const change = patch => {
    if (busy || !accessAvailable) return;
    localEdits.current = true;
    if (Object.hasOwn(patch, 'platform')) {
      platformChosen.current = true;
      setAccountId(value => accounts.some(account => account.id === value && account.platform === patch.platform && canPublishAccount(account, registry)) ? value : '');
    }
    setForm(value => ({ ...value, ...patch })); setSaved(false); setConfirmation(null);
  };
  const work = async (action, kind = 'other') => {
    if (!enabled || !accessAvailable || running.current || !current() || user?.id == null) return;
    running.current = true; setBusy(true); setError(null);
    try { await action(); }
    catch (err) {
      if (!current()) return;
      if (err?.details?.reason === 'FREE_PUBLICATION_LIMIT') setError('exhausted');
      else if (err?.status === 409 && ((kind === 'draft' && raw)
        || (kind === 'createDraft' && ['manual_draft_receipt_conflict', 'manual_draft_receipt_unavailable'].includes(err?.details?.reason)))) {
        setError('changed'); setSaveBlocked(true);
      }
      else if (kind === 'createDraft' && createKey.current && !err?.beforeMutation) {
        setRecovering(true); setError('saveUnconfirmed');
      }
      else setError(err?.localKey || 'saveError');
      if (!err?.beforeMutation && ['draft', 'dispatch'].includes(kind) && (!err?.status || err.status >= 500)) setSaveBlocked(true);
    } finally { running.current = false; if (current()) setBusy(false); }
  };
  const startWorkspace = () => work(async () => {
    const response = unwrap(await api.onboarding.createPersonalWorkspace());
    if (!response?.brandProfileId) throw new Error('Missing workspace');
    if (!current()) return;
    setForm(value => ({ ...value, brandProfileId: response.brandProfileId }));
    await brand.refresh();
    if (current()) await dash.ensureDashboardData?.({ force: true, keys: ['entitlements', 'setupStatus'] });
  });
  const save = event => {
    event?.preventDefault?.();
    const payload = manualDraftPayload(form, Boolean(raw));
    if ((!payload && !recovering) || blocked || saveBlocked) return;
    return work(async () => {
      // Recheck the capability before a write; a stale route must not revive Free access.
      let fresh;
      try { fresh = freePublishingState(unwrap(await api.billing.getEntitlements({ fresh: true }))); }
      catch { throw { localKey: 'accessChanged', beforeMutation: true }; }
      if (!current()) return;
      if (!fresh) { setError('accessChanged'); return; }
      let response;
      let recovered = false;
      if (raw) response = await api.content.updateManualDraft(raw.contentItem.id, payload);
      else {
        if (!createKey.current) {
          createKey.current = readPendingCreate(user, payload.brandProfileId);
          recovered = Boolean(createKey.current);
        }
        if (!createKey.current) {
          // Persist the confirmed request, not a caption-derived identity.
          // A fresh composer can recover it even after the user changes inputs.
          const storage = window.sessionStorage;
          const storageKey = pendingCreateStorageKey(user, payload.brandProfileId);
          const key = crypto.randomUUID();
          const serialized = JSON.stringify({ key, payload });
          storage.setItem(storageKey, serialized);
          if (storage.getItem(storageKey) !== serialized) throw new Error('Pending draft identity unavailable');
          createKey.current = { key, payload, serialized, storageKey, storage };
        }
        recovered ||= recovering;
        try {
          response = await api.content.createManualDraft({ ...createKey.current.payload, idempotencyKey: createKey.current.key });
        } catch (err) {
          // Content's 400 validation response precedes its atomic create. Only
          // this first attempt proves nothing was saved; a later retry can fail
          // validation after an earlier unknown request already committed.
          if (!recovered && err?.status === 400 && err?.code === 'VALIDATION_ERROR' && current()) {
            const { storage, storageKey, serialized } = createKey.current;
            try {
              if (storage.getItem(storageKey) === serialized) {
                storage.removeItem(storageKey);
                if (storage.getItem(storageKey) === null) createKey.current = null;
              }
            } catch { /* Keep recovery required when durable cleanup is unknown. */ }
          }
          throw err;
        }
      }
      const result = unwrap(response);
      if (!current()) return;
      if (!canEditManualDraft(result) || (createKey.current && result.contentItem.brandProfileId !== createKey.current.payload.brandProfileId)) throw new Error('Missing saved draft');
      const savedForm = manualDraftForm(result);
      const keepChanges = recovered && localEdits.current
        && JSON.stringify(payload) !== JSON.stringify(createKey.current.payload);
      setRaw(result);
      setForm(keepChanges ? { ...formRef.current, expectedVersionId: savedForm.expectedVersionId } : savedForm);
      setSaved(!keepChanges); setRecovering(false);
      setNotice(recovered ? (keepChanges ? 'recoveredUnsaved' : 'recoveredDraft') : null);
      if (createKey.current) {
        const { storage, storageKey, serialized } = createKey.current;
        // Do not erase a newer action from another mounted composer. A
        // storage failure leaves the receipt reusable; it cannot create twice.
        try { if (storage.getItem(storageKey) === serialized) storage.removeItem(storageKey); } catch { /* retain safely */ }
      }
      createKey.current = null; // Further saves edit this confirmed draft.
      dash.ensureDashboardData?.({ force: true, keys: ['entitlements'] }).catch(() => {});
    }, raw ? 'draft' : 'createDraft');
  };
  const upload = (event, { replace = false } = {}) => {
    const files = Array.from(event.target.files || []); event.target.value = '';
    if (!files.length || !form.brandProfileId || blocked) return;
    const hasVideo = files.some(file => VIDEO_ACCEPT_SET.has(file.type));
    const existing = replace ? [] : form.media;
    if ((replace && files.length !== 1)
      || existing.some(asset => asset.type === 'video') || (hasVideo && (files.length !== 1 || existing.length))
      || existing.length + files.length > 10
      || files.some(file => !imageTypes.has(file.type) && !VIDEO_ACCEPT_SET.has(file.type))
      || files.some(file => file.size > (VIDEO_ACCEPT_SET.has(file.type) ? MAX_VIDEO_BYTES : 5 * 1024 * 1024))) {
      setError('mediaRules'); return;
    }
    work(async () => {
      // Keep successful uploads if a later file fails; never upload twice silently.
      for (const file of files) {
        let result;
        if (VIDEO_ACCEPT_SET.has(file.type)) {
          const meta = await readVideoMetaAndPoster(file);
          if (!Number.isFinite(meta.duration) || meta.duration <= 0 || meta.duration > MAX_VIDEO_SECONDS) throw { localKey: 'mediaRules' };
          if (!current()) return;
          result = unwrap(await api.videos.upload(file, { fileName: file.name, width: meta.width, height: meta.height,
            durationSeconds: meta.duration, brandProfileId: form.brandProfileId }));
        } else {
          const dataUrl = await fileData(file);
          if (!current()) return;
          result = unwrap(await api.brandAssets.upload({ dataUrl, fileName: file.name, brandProfileId: form.brandProfileId }));
        }
        if (!current()) return;
        const asset = result?.asset || result;
        if (!asset?.id) throw new Error('Missing uploaded asset');
        localEdits.current = true;
        setForm(value => ({ ...value, media: [...(replace ? [] : value.media), { id: asset.id, type: VIDEO_ACCEPT_SET.has(file.type) ? 'video' : 'image',
          url: asset.url || asset.publicUrl || asset.public_url || '', name: file.name,
          posterUrl: asset.posterUrl || asset.poster_url || null, durationSeconds: asset.durationSeconds, mimeType: asset.mimeType || file.type }] }));
        setSaved(false); setConfirmation(null);
      }
    });
  };
  const prepareDispatch = () => work(async () => {
    if (!saved || blocked || saveBlocked || !raw) return;
    const account = accounts.find(value => value.id === accountId);
    if (!canPublishAccount(account, registry)) { setError('chooseDestination'); return; }
    const payload = manualSchedulePayload(raw, account, scheduledAt);
    if (!payload) { setError('chooseDestination'); return; }
    const fresh = freePublishingState(unwrap(await api.billing.getEntitlements({ fresh: true })));
    if (!current()) return;
    if (!fresh) { setError('accessChanged'); return; }
    const usage = unwrap(await api.billing.getFreePublishingUsage(payload.scheduledAt));
    if (!current()) return;
    const selected = freePublishingState({ freePublishing: { ...fresh, usage } });
    if (!selected) { setError('accessChanged'); return; }
    setAllowance(selected);
    if (selected.usage.remaining === 0) { setError('exhausted'); return; }
    setConfirmation({ payload, label: `${platformDisplayName(account.platform)} · ${account.displayName || account.platform}` });
  });
  const dispatch = () => work(async () => {
    if (!confirmation || !saved || blocked || saveBlocked) return;
    await api.scheduler.create(confirmation.payload);
    if (!current()) return;
    setConfirmation(null);
    // Acceptance reserves capacity; refresh it without treating a failed read
    // as a failed publication or retrying the accepted scheduler request.
    dash.invalidateResource?.('entitlements', { fresh: true }).catch(() => {});
    // The publication receipt owns the status; an accepted request is not a success claim.
    navigate(`/app/post/${encodeURIComponent(raw.contentItem.id)}`);
  }, 'dispatch');
  const selectAccount = nextId => {
    if (busy || !accessAvailable) return;
    platformChosen.current = true;
    const account = accounts.find(value => value.id === nextId);
    if (account && !canPublishAccount(account, registry)) return;
    if (account && account.platform !== form.platform) change({ platform: account.platform });
    setAccountId(account ? nextId : ''); setConfirmation(null);
  };
  const changeSchedule = value => { if (!busy && accessAvailable) { setScheduledAt(value); setConfirmation(null); } };
  return {
    raw, form, loading, busy, saved, blocked, saveBlocked, accounts, accountId, notice, recovering,
    scheduledAt, confirmation, allowance, error: accessAvailable ? error : 'accessChanged',
    disabled: busy || blocked || saveBlocked || !accessAvailable || user?.id == null,
    canSave: !blocked && !saveBlocked && accessAvailable && user?.id != null && (recovering || Boolean(manualDraftPayload(form, Boolean(raw)))),
    change, save, upload, startWorkspace, prepareDispatch, dispatch, selectAccount, changeSchedule,
    cancelConfirmation: () => setConfirmation(null),
    accountReady: account => canPublishAccount(account, registry),
  };
}
